// src/services/milhasCalc.js
// Calculadora de vuelos por millas (admin). Funciones puras, sin UI ni red.
// Costo = millas/1000 × milheiro + taxa de embarque. El precio al cliente se
// arma aplicando tu margen SOBRE EL TOTAL (millas + taxa).

export const PASO_MILHEIRO = 0.25;
export const MARGENES = [10, 15, 20];

// Rango habitual del milheiro (R$ por cada 1.000 millas) según la compañía.
// Son puntos de partida: el admin los puede mover desde la pantalla.
export const AEROLINEAS = [
  { id: 'gol', label: 'Gol', programa: 'Smiles', min: 15, max: 17 },
  { id: 'latam', label: 'Latam', programa: 'Latam Pass', min: 16, max: 18 },
  { id: 'azul', label: 'Azul', programa: 'Azul Fidelidade', min: 15, max: 17 },
  { id: 'otra', label: 'Otra', programa: '', min: 14, max: 20 },
];

export function idAerolinea(nombre) {
  const n = String(nombre || '').toLowerCase();
  if (n.includes('gol')) return 'gol';
  if (n.includes('latam') || n.includes('tam')) return 'latam';
  if (n.includes('azul')) return 'azul';
  return 'otra';
}

// El milheiro siempre termina en .00 / .25 / .50 / .75
export function ajustarMilheiro(v) {
  const n = Number(String(v ?? '').replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n / PASO_MILHEIRO) * PASO_MILHEIRO;
}

export function valoresMilheiro(min, max) {
  const a = ajustarMilheiro(min);
  const b = ajustarMilheiro(max);
  if (a === null || b === null || b < a) return [];
  const out = [];
  for (let v = a; v <= b + 1e-9; v += PASO_MILHEIRO) out.push(Math.round(v * 100) / 100);
  return out;
}

const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Devuelve null mientras falten las millas o el milheiro.
export function calcularMilhas({ millas, milheiro, taxa, margenPct, pasajeros = 1, redondear = true }) {
  const m = num(millas);
  const mil = num(milheiro);
  if (m <= 0 || mil <= 0) return null;
  const pax = Math.max(1, Math.floor(num(pasajeros)) || 1);
  const costoMillas = r2((m / 1000) * mil * pax);
  const costoTaxa = r2(num(taxa) * pax);
  const costo = r2(costoMillas + costoTaxa);
  const bruto = r2(costo * (1 + num(margenPct) / 100));
  const precio = redondear ? Math.ceil(bruto) : bruto;
  const ganancia = r2(precio - costo);
  return {
    costoMillas, costoTaxa, costo, precio, ganancia,
    gananciaPct: costo > 0 ? r2((ganancia / costo) * 100) : 0,
  };
}

export const fmtBRL = (n, dec = 2) => Number(n || 0).toLocaleString('pt-BR', {
  minimumFractionDigits: dec, maximumFractionDigits: dec,
});

// ── Aeropuertos frecuentes: IATA → ciudad (para el título grande) ─────
export const CIUDADES = {
  CWB: 'Curitiba', FLN: 'Florianópolis', MAO: 'Manaus', GRU: 'São Paulo', CGH: 'São Paulo', VCP: 'Campinas',
  GIG: 'Rio de Janeiro', SDU: 'Rio de Janeiro', BSB: 'Brasília', SSA: 'Salvador', REC: 'Recife', FOR: 'Fortaleza',
  NAT: 'Natal', MCZ: 'Maceió', BEL: 'Belém', POA: 'Porto Alegre', CNF: 'Belo Horizonte', PLU: 'Belo Horizonte',
  VIX: 'Vitória', GYN: 'Goiânia', CGB: 'Cuiabá', BVB: 'Boa Vista', PVH: 'Porto Velho', RBR: 'Rio Branco',
  MCP: 'Macapá', SLZ: 'São Luís', THE: 'Teresina', JPA: 'João Pessoa', AJU: 'Aracaju', IGU: 'Foz do Iguaçu',
  NVT: 'Navegantes', JOI: 'Joinville', LDB: 'Londrina', CGR: 'Campo Grande', PMW: 'Palmas', STM: 'Santarém',
  BPS: 'Porto Seguro', IOS: 'Ilhéus', FEN: 'Fernando de Noronha', MGF: 'Maringá', XAP: 'Chapecó',
  UDI: 'Uberlândia', RAO: 'Ribeirão Preto', CXJ: 'Caxias do Sul', JDO: 'Juazeiro do Norte',
};

// Texto -> número, entendiendo el formato brasileño ("23.528" = 23528, "48,63" = 48.63).
export function numeroBR(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  let s = String(v).replace(/[^\d.,]/g, '');
  if (!s) return null;
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

// ── Vuelos que no son con millas: costo real según la fuente ─────────
// Comprarviagem muestra un precio de lista que ya incluye tu comisión: lo que pagas
// de verdad es lista × factor (el factor sale de los Ajustes del Cotizador, 0,9021).
// Speedmilhas (Total Pix) y Resumen del pedido ya son el costo real.
export const FUENTES = [
  { id: 'millas', label: 'Millas' },
  { id: 'comprarviagem', label: 'Comprarviagem', campo: 'Precio de lista', ayuda: 'El precio de lista ya incluye tu comisión.', usaFactor: true },
  { id: 'speedmilhas', label: 'Speedmilhas', campo: 'Total Pix', ayuda: 'Es el Total Pix tal cual.' },
  { id: 'resumen', label: 'Resumen del pedido', campo: 'Total del resumen', ayuda: 'Es el total del resumen tal cual.' },
];

export const FACTOR_COMPRARVIAGEM = 0.9021;

export function costoDeFuente({ fuente, precio, factor = FACTOR_COMPRARVIAGEM }) {
  const p = Number(precio);
  if (!Number.isFinite(p) || p <= 0) return null;
  const f = FUENTES.find((x) => x.id === fuente);
  if (!f || f.id === 'millas') return null;
  return r2(f.usaFactor ? p * (Number(factor) || FACTOR_COMPRARVIAGEM) : p);
}

// Precio al cliente a partir del costo real. Si el admin escribe su propio precio
// (`precioManual`) manda ese; si no, se sugiere costo × (1 + margen), redondeado hacia arriba.
export function calcularPrecio({ costo, margenPct, precioManual, redondear = true }) {
  const c = Number(costo);
  if (!Number.isFinite(c) || c <= 0) return null;
  const manual = precioManual === '' || precioManual === null || precioManual === undefined ? null : Number(precioManual);
  let precio;
  if (manual !== null && Number.isFinite(manual) && manual > 0) precio = r2(manual);
  else {
    const bruto = r2(c * (1 + num(margenPct) / 100));
    precio = redondear ? Math.ceil(bruto) : bruto;
  }
  const ganancia = r2(precio - c);
  return { costo: r2(c), precio, ganancia, gananciaPct: r2((ganancia / c) * 100) };
}

// "8H 00M" / "8h" / "8:00" -> "8h 00min"
export function normalizarDuracion(txt) {
  const t = String(txt || '').trim();
  if (!t) return '';
  const m = t.match(/(\d+)\s*(?:h|:)\s*(\d+)?\s*(?:m|min)?/i);
  if (!m) return t;
  return `${Number(m[1])}h ${String(m[2] || 0).padStart(2, '0')}min`;
}

// ── Orçamento con varias líneas (vuelo, hotel, servicios, ómnibus) ────
// Cada línea trae su costo real y, opcionalmente, un precio escrito por el admin; si no hay
// precio escrito se sugiere costo × (1 + margen), redondeado hacia arriba. El total es la suma.
// `completo` es false si alguna línea no tiene ni costo ni precio.
export function totalesOrcamento({ lineas, margenPct }) {
  const out = (lineas || []).map((l) => {
    const costo = Number(l.costo);
    const tieneCosto = Number.isFinite(costo) && costo > 0;
    const manual = l.precioManual === '' || l.precioManual === null || l.precioManual === undefined ? null : Number(l.precioManual);
    const tieneManual = manual !== null && Number.isFinite(manual) && manual > 0;
    if (tieneCosto) {
      const r = calcularPrecio({ costo, margenPct, precioManual: tieneManual ? manual : '' });
      return { ...l, costo: r.costo, precio: r.precio, ganancia: r.ganancia, completa: true };
    }
    // Sin costo: si hay precio escrito, vale como precio con ganancia desconocida (se cuenta toda).
    if (tieneManual) return { ...l, costo: 0, precio: r2(manual), ganancia: r2(manual), completa: true };
    return { ...l, costo: 0, precio: 0, ganancia: 0, completa: false };
  });
  const costo = r2(out.reduce((a, l) => a + l.costo, 0));
  const precio = r2(out.reduce((a, l) => a + l.precio, 0));
  const ganancia = r2(precio - costo);
  return {
    lineas: out, costo, precio, ganancia,
    gananciaPct: costo > 0 ? r2((ganancia / costo) * 100) : 0,
    completo: out.length > 0 && out.every((l) => l.completa),
  };
}

// Noches entre dos fechas ISO (entrada y salida). 0 si falta alguna o la salida no es posterior.
export function nochesEntre(entradaIso, salidaIso) {
  const f = (iso) => { const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : null; };
  const a = f(entradaIso); const b = f(salidaIso);
  if (a === null || b === null || b <= a) return 0;
  return Math.round((b - a) / 86400000);
}
